import ArrowBackLight from "@assets/navigation/back.svg";
import LightCustomButton from "@components/buttons/LightCustomButton";
import { TextMultiSelect } from "@components/dropdowns/TextMultiSelectDropown";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import ToggleInput from "@components/inputs/ToggleInput";
import { commonApiServices } from "@services/apis/Common";
import { primeServices } from "@services/apis/Prime";
import { CountryState } from "@services/models/Common/GetCountryStates";
import {
  PrimeIndiaVpnConfig,
  UpdatePrimeIndiaVpnConfigRequest,
} from "@services/models/Prime/PrimeIndiaVpnConfigModel";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import ConstantFunctions from "src/utils/constants_functions";
import useSnackbarMessage from "../../utils/HandleSuccessErrorSnackbars";
import NumericChipInput from "./components/NumericChipInput";
import PrimeIndiaVpnSettingsSkeleton from "./components/PrimeIndiaVpnSettingsSkeleton";

type FormState = Omit<PrimeIndiaVpnConfig, "updatedBy" | "updatedAt">;

const DEFAULT_FORM: FormState = {
  allow_vpn: false,
  bypass_vpn_merchants: [],
  allow_outside_india: false,
  bypass_outside_india_merchants: [],
  restricted_states: [],
  prime_trade_buy_max_requests: 5,
  prime_trade_buy_window_ms: 3600000,
  prime_trade_buy_retry_after: 3600,
  prime_trade_buy_daily_max_requests: 10,
  rate_limit_bypass_merchants: [],
  rate_limit_bypass_users: [],
};

const getApiErrorMessage = (error: unknown, fallback: string) => {
  const err = error as {
    response?: { data?: { msg?: string } };
    message?: string;
  };
  return err?.response?.data?.msg || err?.message || fallback;
};

const getStateField = (state: CountryState, keys: string[]) => {
  const record = state as CountryState & Record<string, string | undefined>;
  for (const key of keys) {
    const value = record[key];
    if (value) return value.trim();
  }
  return "";
};

const getStateName = (state: CountryState) =>
  getStateField(state, ["regionName", "regionName"]);

const getStateCode = (state: CountryState) =>
  getStateField(state, ["regionCode", "regionCode"]);

const getStateType = (state: CountryState) =>
  getStateField(state, ["regionType", "regionType"]);

const buildForm = (data?: Partial<PrimeIndiaVpnConfig>): FormState => ({
  allow_vpn: data?.allow_vpn ?? DEFAULT_FORM.allow_vpn,
  bypass_vpn_merchants: data?.bypass_vpn_merchants ?? [],
  allow_outside_india:
    data?.allow_outside_india ?? DEFAULT_FORM.allow_outside_india,
  bypass_outside_india_merchants: data?.bypass_outside_india_merchants ?? [],
  restricted_states: data?.restricted_states ?? [],
  prime_trade_buy_max_requests:
    data?.prime_trade_buy_max_requests ??
    DEFAULT_FORM.prime_trade_buy_max_requests,
  prime_trade_buy_window_ms:
    data?.prime_trade_buy_window_ms ?? DEFAULT_FORM.prime_trade_buy_window_ms,
  prime_trade_buy_retry_after:
    data?.prime_trade_buy_retry_after ??
    DEFAULT_FORM.prime_trade_buy_retry_after,
  prime_trade_buy_daily_max_requests:
    data?.prime_trade_buy_daily_max_requests ??
    DEFAULT_FORM.prime_trade_buy_daily_max_requests,
  rate_limit_bypass_merchants: data?.rate_limit_bypass_merchants ?? [],
  rate_limit_bypass_users: data?.rate_limit_bypass_users ?? [],
});

const toPayload = (form: FormState): UpdatePrimeIndiaVpnConfigRequest => ({
  allow_vpn: form.allow_vpn,
  bypass_vpn_merchants: form.bypass_vpn_merchants,
  allow_outside_india: form.allow_outside_india,
  bypass_outside_india_merchants: form.bypass_outside_india_merchants,
  restricted_states: form.restricted_states,
  prime_trade_buy_max_requests: form.prime_trade_buy_max_requests,
  prime_trade_buy_window_ms: form.prime_trade_buy_window_ms,
  prime_trade_buy_retry_after: form.prime_trade_buy_retry_after,
  prime_trade_buy_daily_max_requests: form.prime_trade_buy_daily_max_requests,
  rate_limit_bypass_merchants: form.rate_limit_bypass_merchants,
  rate_limit_bypass_users: form.rate_limit_bypass_users,
});

const mapRestrictedStates = (
  saved: string[],
  states: CountryState[],
): string[] =>
  saved.map((value) => {
    const key = value.trim().toLowerCase();
    const byName = states.find(
      (state) => getStateName(state).toLowerCase() === key,
    );
    if (byName) return getStateName(byName).toLowerCase();
    const byCode = states.find(
      (state) => getStateCode(state).toLowerCase() === key,
    );
    if (byCode) return getStateName(byCode).toLowerCase();
    return key;
  });

const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col">
    <span className="text-[12px] font-semibold pb-[8px]">{title}</span>
    <div className="flex flex-col border border-border-subtle bg-bg-page-body rounded-[8px] p-[12px] gap-[12px]">
      {children}
    </div>
  </div>
);

const ToggleRow = ({
  title,
  enabledDescription,
  disabledDescription,
  isToggled,
  onToggle,
}: {
  title: string;
  enabledDescription: string;
  disabledDescription: string;
  isToggled: boolean;
  onToggle: () => void;
}) => (
  <div className="flex flex-row justify-between items-start gap-4">
    <div className="flex flex-col gap-[2px] pr-2">
      <span className="font-medium text-[10px] text-text-base">{title}</span>
      <span className="text-[10px] text-text-header">
        {isToggled ? enabledDescription : disabledDescription}
      </span>
    </div>
    <ToggleInput isToggled={isToggled} onToggle={onToggle} size="sm" />
  </div>
);

const PrimeIndiaVpnSettings = ({
  isMobile = false,
}: {
  isMobile?: boolean;
}) => {
  const navigate = useNavigate();
  const { handleError, handleSuccess } = useSnackbarMessage();
  const handleErrorRef = useRef(handleError);
  const handleSuccessRef = useRef(handleSuccess);
  handleErrorRef.current = handleError;
  handleSuccessRef.current = handleSuccess;
  const constantFunctions = useMemo(() => new ConstantFunctions(), []);

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [originalForm, setOriginalForm] = useState<FormState>(DEFAULT_FORM);
  const [states, setStates] = useState<CountryState[]>([]);
  const [updatedBy, setUpdatedBy] = useState("");
  const [updatedAt, setUpdatedAt] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState("");

  const applyConfig = useCallback(
    (data: PrimeIndiaVpnConfig, countryStates: CountryState[]) => {
      const nextForm = buildForm({
        ...data,
        restricted_states: mapRestrictedStates(
          data.restricted_states || [],
          countryStates,
        ),
      });
      setForm(nextForm);
      setOriginalForm(nextForm);
      setUpdatedBy(data.updatedBy || "");
      setUpdatedAt(data.updatedAt || "");
    },
    [],
  );

  const loadPage = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError("");
      const [statesRes, configRes] = await Promise.all([
        commonApiServices.getCountryStates("IN"),
        primeServices.getPrimeIndiaVpnConfig(),
      ]);

      if (statesRes.success === false) {
        throw new Error(statesRes.msg || "Failed to fetch Indian states");
      }
      if (configRes.success === false) {
        throw new Error(configRes.msg || "Failed to fetch VPN config");
      }

      const countryStates = statesRes.data || [];
      setStates(countryStates);
      applyConfig(configRes.data, countryStates);
    } catch (error) {
      const message = getApiErrorMessage(
        error,
        "Failed to load Prime India IP & VPN settings",
      );
      setLoadError(message);
      handleErrorRef.current({ message });
    } finally {
      setIsLoading(false);
    }
  }, [applyConfig]);

  useEffect(() => {
    void loadPage();
  }, []);

  const isDirty = useMemo(
    () =>
      JSON.stringify(toPayload(form)) !==
      JSON.stringify(toPayload(originalForm)),
    [form, originalForm],
  );

  const stateList = useMemo(
    () =>
      states
        .filter((state) => getStateName(state))
        .map((state, index) => {
          const name = getStateName(state);
          const type = getStateType(state);
          return {
            id: index + 1,
            name: type ? `${name} (${type})` : name,
            stateKey: name.toLowerCase(),
          };
        }),
    [states],
  );

  const selectedStateIds = useMemo(
    () =>
      form.restricted_states
        .map(
          (saved) =>
            stateList.find(
              (state) => state.stateKey === saved.trim().toLowerCase(),
            )?.id,
        )
        .filter((id): id is number => id !== undefined),
    [form.restricted_states, stateList],
  );

  const handleSave = async () => {
    if (
      form.prime_trade_buy_max_requests <= 0 ||
      form.prime_trade_buy_window_ms <= 0 ||
      form.prime_trade_buy_retry_after < 0 ||
      form.prime_trade_buy_daily_max_requests <= 0
    ) {
      handleErrorRef.current({
        message: "Rate limit fields must be valid numbers greater than 0",
      });
      return;
    }

    try {
      setIsSaving(true);
      const response = await primeServices.updatePrimeIndiaVpnConfig(
        toPayload(form),
      );
      if (response.success === false) {
        handleErrorRef.current({
          message: response.msg || "Failed to update VPN config",
        });
        return;
      }
      applyConfig(response.data || { ...form, updatedBy, updatedAt }, states);
      handleSuccessRef.current({
        message: response.msg || "Prime Indian VPN config updated successfully",
      });
    } catch (error) {
      handleErrorRef.current({
        message: getApiErrorMessage(error, "Failed to update VPN config"),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const headerClass = isMobile ? "h-[56px] px-4" : "h-[70px] pl-[20px] pr-4";

  return (
    <div className="flex flex-col h-full">
      <div
        className={`${headerClass} border-b border-border-subtle bg-bg-page-body flex flex-row sticky top-0 bg-white z-10 w-full`}
      >
        <div className="flex flex-grow justify-between items-center w-full gap-2">
          <div className="flex gap-2 items-center min-w-0">
            <img
              src={ArrowBackLight}
              alt="Back"
              className="cursor-pointer shrink-0"
              onClick={() => navigate("/prime-order-flow")}
            />
            <p
              className={`text-text-base font-semibold truncate ${
                isMobile ? "text-[13px]" : "text-[14px]"
              }`}
            >
              Prime India IP & VPN Settings
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <LightCustomButton
              text="Reset"
              isActive={!isLoading && isDirty && !isSaving}
              onClick={() => setForm(originalForm)}
            />
            <LightCustomButton
              text={isSaving ? "Saving..." : "Save Changes"}
              isActive={!isLoading && isDirty && !isSaving}
              isButtonLoading={isSaving}
              onClick={handleSave}
            />
          </div>
        </div>
      </div>

      <div
        className={`flex-1 overflow-auto ${isMobile ? "p-3" : "p-4"}`}
        style={{
          height: isMobile ? "calc(100dvh - 112px)" : "calc(100dvh - 70px)",
        }}
      >
        {isLoading ? (
          <PrimeIndiaVpnSettingsSkeleton />
        ) : loadError ? (
          <div className="flex flex-col gap-3">
            <p className="text-[12px] text-red-600">{loadError}</p>
            <LightCustomButton
              text="Retry"
              isActive={true}
              onClick={loadPage}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-[16px] max-w-[820px]">
            {(updatedBy || updatedAt) && (
              <p className="text-[10px] text-text-header">
                Last updated
                {updatedBy ? ` by ${updatedBy}` : ""}
                {updatedAt
                  ? ` at ${constantFunctions.formatDateTime(updatedAt)}`
                  : ""}
              </p>
            )}

            <SectionCard title="VPN / PROXY ACCESS">
              <ToggleRow
                title="Allow VPN"
                enabledDescription="true = Allow VPN/Proxy/Tor/Hosting IPs"
                disabledDescription="false = Block VPN/Proxy/Tor/Hosting IPs"
                isToggled={form.allow_vpn}
                onToggle={() =>
                  setForm((prev) => ({ ...prev, allow_vpn: !prev.allow_vpn }))
                }
              />
              <NumericChipInput
                label="Bypass VPN Merchants"
                values={form.bypass_vpn_merchants}
                onChange={(values) =>
                  setForm((prev) => ({ ...prev, bypass_vpn_merchants: values }))
                }
                placeholder="Enter merchant ID and press Enter"
                helperText="Merchant IDs that should bypass the VPN block"
              />
            </SectionCard>

            <SectionCard title="OUTSIDE INDIA ACCESS">
              <ToggleRow
                title="Allow Outside India"
                enabledDescription="true = Allow transactions from outside India"
                disabledDescription="false = Block transactions originating outside India"
                isToggled={form.allow_outside_india}
                onToggle={() =>
                  setForm((prev) => ({
                    ...prev,
                    allow_outside_india: !prev.allow_outside_india,
                  }))
                }
              />
              <NumericChipInput
                label="Bypass Outside India Merchants"
                values={form.bypass_outside_india_merchants}
                onChange={(values) =>
                  setForm((prev) => ({
                    ...prev,
                    bypass_outside_india_merchants: values,
                  }))
                }
                placeholder="Enter merchant ID and press Enter"
                helperText="Merchant IDs allowed to transact from outside India"
              />
            </SectionCard>

            <SectionCard title="RESTRICTED STATES">
              <TextMultiSelect
                listOfValues={stateList}
                selectedValues={selectedStateIds}
                onChange={(values) =>
                  setForm((prev) => ({
                    ...prev,
                    restricted_states: values
                      .map(
                        (id) =>
                          stateList.find((state) => state.id === id)?.stateKey,
                      )
                      .filter((name): name is string => Boolean(name)),
                  }))
                }
                label="Indian States / UTs"
                enableSearch={true}
                color="bg-page-body"
                baseColor="bg-page-body"
                borderColor="border-subtle"
                expandedBorderColor="border-button-primary"
              />
              <span className="text-text-header text-[10px] -mt-[4px]">
                States and union territories that should be restricted
              </span>
            </SectionCard>

            <SectionCard title="TRADE BUY RATE LIMITS">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-[12px]">
                <GlobalTextInputField
                  type="number"
                  title="Max Requests"
                  allowDecimal={false}
                  value={String(form.prime_trade_buy_max_requests)}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      prime_trade_buy_max_requests:
                        Number(event.target.value) || 0,
                    }))
                  }
                  placeholder="5"
                />
                <GlobalTextInputField
                  type="number"
                  title="Window (ms)"
                  allowDecimal={false}
                  value={String(form.prime_trade_buy_window_ms)}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      prime_trade_buy_window_ms:
                        Number(event.target.value) || 0,
                    }))
                  }
                  placeholder="3600000"
                />
                <GlobalTextInputField
                  type="number"
                  title="Retry After (seconds)"
                  allowDecimal={false}
                  value={String(form.prime_trade_buy_retry_after)}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      prime_trade_buy_retry_after:
                        Number(event.target.value) || 0,
                    }))
                  }
                  placeholder="3600"
                />
                <GlobalTextInputField
                  type="number"
                  title="Daily Max Requests"
                  allowDecimal={false}
                  value={String(form.prime_trade_buy_daily_max_requests)}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      prime_trade_buy_daily_max_requests:
                        Number(event.target.value) || 0,
                    }))
                  }
                  placeholder="10"
                />
              </div>
              <span className="text-text-header text-[10px]">
                Hourly and daily buy-trade request limits per client IP.
                3600000ms = 1 hour.
              </span>
            </SectionCard>

            <SectionCard title="RATE LIMIT BYPASS">
              <NumericChipInput
                label="Bypass Merchants"
                values={form.rate_limit_bypass_merchants}
                onChange={(values) =>
                  setForm((prev) => ({
                    ...prev,
                    rate_limit_bypass_merchants: values,
                  }))
                }
                placeholder="Enter merchant ID and press Enter"
                helperText="Merchant IDs that bypass trade buy rate limits and VPN checks"
              />
              <NumericChipInput
                label="Bypass Users"
                values={form.rate_limit_bypass_users}
                onChange={(values) =>
                  setForm((prev) => ({
                    ...prev,
                    rate_limit_bypass_users: values,
                  }))
                }
                placeholder="Enter user ID and press Enter"
                helperText="User IDs that bypass trade buy rate limits and VPN checks"
              />
            </SectionCard>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrimeIndiaVpnSettings;
